import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";

/**
 * Handles trial authenticity
 *
 * @param {Object} args
 * @param {Object} args.volumeDiscountRepository - VolumeDiscountRepository
 * @param {Object} args.volumeDiscountUserRepository - VolumeDiscountUserRepository
 * @returns {Promise<number>}
 */
export async function getTrialStartTimestamp(args) {
  const privateKey = "d4b6b2f3-8b1e-4d7e-8b1e-4d7e8b1e4d7e";
  const volumeDiscountUsers = await args.volumeDiscountUserRepository.getAll();
  const expiredDateIso = new Date("0000").getTime();

  if (volumeDiscountUsers.items.length <= 0) {
    const volumeDiscounts = await args.volumeDiscountRepository.getAll();

    // If there are any volume discounts then it means user manually deleted user data row
    if (volumeDiscounts.items.length > 0) {
      return expiredDateIso;
    }

    const timestamp = new Date().getTime();

    const siteMetaConnection = createSiteMetaConnection({
      createdAtTimestamp: timestamp,
      privateKey,
    });

    // Otherwise its first user visit and we need to create a new user data row
    const user = await args.volumeDiscountUserRepository.create({
      userId: uuidv4(), // This is a placeholder, the actual user ID should be fetched from the user's session
      siteMetaConnection,
      settings: {
        isHowToUseOpen: true,
      },
    });

    console.log("User created", user);

    return timestamp;
  }

  const volumeDiscountUser = volumeDiscountUsers.items[0];

  try {
    const siteMetaConnection = decryptSiteMetaConnection(
      volumeDiscountUser.siteMetaConnection,
      privateKey
    );

    return siteMetaConnection.createdAtTimestamp;
  } catch (e) {
    console.error(e);

    return expiredDateIso;
  }
}

/**
 * @param {number} createdTimestamp
 * @returns {{startTimestamp: number, endTimestamp: number, durationMs: number, isActive: boolean}}
 */
export function getTrialEndTimestamp(createdTimestamp) {
  const trialStartTimestamp = new Date(createdTimestamp).getTime();

  const trialDurationMs = 3 * 24 * 60 * 60 * 1000; // 3 days

  return {
    startTimestamp: trialStartTimestamp,
    endTimestamp: trialStartTimestamp + trialDurationMs,
    durationMs: trialDurationMs,
    isActive: Date.now() < trialStartTimestamp + trialDurationMs,
  };
}

/**
 * @param {Object} args
 * @param {string} args.privateKey
 * @param {number} args.createdAtTimestamp
 * @returns {string}
 */
function createSiteMetaConnection(args) {
  const { privateKey, createdAtTimestamp } = args;

  const meta = {
    createdAtTimestamp,
  };

  return CryptoJS.AES.encrypt(JSON.stringify(meta), privateKey).toString();
}

/**
 * @param {string} siteMetaConnection
 * @param {string} privateKey
 * @returns {{ createdAtTimestamp: number }}
 */
function decryptSiteMetaConnection(siteMetaConnection, privateKey) {
  const bytes = CryptoJS.AES.decrypt(siteMetaConnection, privateKey);
  const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

  return JSON.parse(decryptedData);
}
